<template>
  <v-card>
    <v-card-text>
      <v-card-title>
        <h1 class="text-h4 mr-2">Authenticated Domains</h1>
        <RefreshSpinner v-model="isRefreshingData" @click="refreshList"></RefreshSpinner>
        <v-spacer></v-spacer>
        <v-btn color="primary" small @click.once="revalidateAll">Revalidate All Domains</v-btn>
        <v-spacer></v-spacer>
        <v-text-field @keydown.native.escape="search = ''" label="Search" single-line clearable hide-details
          v-model="search"></v-text-field>
      </v-card-title>
    </v-card-text>
    <v-data-table :headers="tableColumnsStatic" :items="queriedData" item-key="id" class="elevation-1"
      style="light-green-lighten-4" :loading="isReauthenticating">
      <template v-slot:item.senderConfigured="{ item }">
        <v-chip medium v-if="item.senderConfigured" color="green lighten-1" text-color="white">
          {{ item.senderConfigured | boolToYesNo }}
        </v-chip>
        <v-chip medium v-else color="orange lighten-1" text-color="white">
          {{ item.senderConfigured | boolToYesNo }}
        </v-chip>
      </template>
      <template v-slot:item.senderValid="{ item }">
        <v-chip medium v-if="item.senderValid" color="green lighten-1" text-color="white">
          {{ item.senderValid | boolToYesNo }}
        </v-chip>
        <span v-else-if="!item.senderValid && !item.senderConfigured">&nbsp;</span>
        <v-chip medium v-else color="red lighten-1" text-color="white">
          {{ item.senderValid | boolToYesNo }}
        </v-chip>
      </template>
      <template v-slot:item.senderLastValidationAttemptAt="{ item }">
        <template v-if="item.senderConfigured">
          <span>{{ item.senderLastValidationAttemptAt | timestampToDateTime }}</span>&nbsp;
          <v-chip small label>{{ item.senderLastValidationAttemptAt | timestampToRelativeTime }}</v-chip>
        </template>
      </template>

      <template v-slot:item.linkConfigured="{ item }">
        <v-chip medium v-if="item.linkConfigured" color="green lighten-1" text-color="white">
          {{ item.linkConfigured | boolToYesNo }}
        </v-chip>
        <v-chip medium v-else color="red lighten-1" text-color="white">
          {{ item.linkConfigured | boolToYesNo }}
        </v-chip>
      </template>
      <template v-slot:item.linkValid="{ item }">
        <v-chip medium v-if="item.linkValid" color="green lighten-1" text-color="white">
          {{ item.linkValid | boolToYesNo }}
        </v-chip>
        <span v-else-if="!item.linkValid && !item.linkConfigured">&nbsp;</span>
        <v-chip medium v-else color="red lighten-1" text-color="white">
          {{ item.linkValid | boolToYesNo }}
        </v-chip>
      </template>
      <template v-slot:item.linkLastValidationAttemptAt="{ item }">
        <template v-if="item.linkConfigured">
          <span>{{ item.linkLastValidationAttemptAt | timestampToDateTime }}</span>&nbsp;
          <v-chip small label>{{ item.linkLastValidationAttemptAt | timestampToRelativeTime }}</v-chip>
        </template>
      </template>

      <template v-slot:item.actions="{ item }">
        <template v-for="(rowAction, index) in recordActions">
          <v-tooltip top :key="index">
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" medium class="mr-2" @click="executeAction(rowAction.action, item)">
                {{ rowAction.icon }}
              </v-icon>
            </template>
            <span>{{ rowAction.title }}</span>
          </v-tooltip>
        </template>
      </template>
    </v-data-table>
    <!-- View Domain -->
    <div class="text-center">
      <v-bottom-sheet v-model="sheet" inset>
        <v-sheet class="text-center" height="80vh">
          <v-toolbar color="secondary" dense dark>
            <v-toolbar-title>{{ currentAuthenticatedDomain.domain }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="sheet = !sheet">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card class="mx-auto" min-width="100%">
            <v-card-text>
              <v-card-title>
                <h2 class="text-h5 text-center">Sender Details</h2>
              </v-card-title>
              <v-row>
                <v-col cols="4">
                  <!-- Domain Valid -->
                  <v-list-item>
                    <v-list-item-content two-line>
                      <v-list-item-title>
                        Sender is Valid
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span :class="currentAuthenticatedDomain.senderValid ? 'green--text' : 'red--text'">
                          {{ currentAuthenticatedDomain.senderValid | boolToYesNo }}
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Domain Validated At -->
                  <v-list-item>
                    <v-list-item-content two-line>
                      <v-list-item-title>Sender Last Validated At</v-list-item-title>
                      <v-list-item-subtitle>
                        <span>
                          {{ currentAuthenticatedDomain.senderLastValidationAttemptAt | timestampToDateTime }}
                        </span>
                        &nbsp;
                        <v-chip small>
                          {{ currentAuthenticatedDomain.senderLastValidationAttemptAt | timestampToRelativeTime }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Sendgrid ID -->
                  <v-list-item v-if="currentAuthenticatedDomain.senderConfigured">
                    <v-list-item-content two-line>
                      <v-list-item-title>
                        Sender Sendgrid ID
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ currentAuthenticatedDomain.senderConfigured }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Subdomain -->
                  <v-list-item v-if="currentAuthenticatedDomain.senderConfigured">
                    <v-list-item-content two-line>
                      <v-list-item-title>
                        Sender Subdomain
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ currentAuthenticatedDomain.senderAuth.subdomain }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Automatic Security -->
                  <v-list-item v-if="currentAuthenticatedDomain.senderConfigured">
                    <v-list-item-content two-line>
                      <v-list-item-title>
                        Sender Automatic Security
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ currentAuthenticatedDomain.senderAuth.automatic_security | boolToYesNo }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Custom SPF -->
                  <v-list-item v-if="currentAuthenticatedDomain.senderConfigured">
                    <v-list-item-content two-line>
                      <v-list-item-title>
                        Sender Custom SPF
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ currentAuthenticatedDomain.senderAuth.custom_spf | boolToYesNo }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <!-- DNS SPF,DKIM -->
                <v-col cols="6" v-if="currentAuthenticatedDomain.senderConfigured">
                  <h3>SPF, DKIM & MAIL DNS</h3>
                  <v-list-item v-for="(record, key, idx) in currentAuthenticatedDomain.senderAuth.dns" :key="idx">
                    <v-list-item-content three-line>
                      <v-list-item-title>
                        <v-chip label small v-if="record.valid" color="green lighten-1" text-color="white">Valid</v-chip>
                        <v-chip label small v-else color="red lighten-1" text-color="white">Invalid</v-chip>
                        {{ key }}
                        <v-chip small label>{{ record.type | uppercase }}</v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="mb-2 mt-2">
                        <span class="font-weight-bold mr-1">DNS Key: </span>{{ record.host }}
                        <v-btn x-small
                          @click="copyToClipboard(extractSubdomain(record.host, currentAuthenticatedDomain.domain))">Copy</v-btn>
                      </v-list-item-subtitle>
                      <v-list-item-subtitle>
                        <span class="font-weight-bold mr-1">DNS Value:</span>{{ record.data }}
                        <v-btn x-small @click="copyToClipboard(record.data)">Copy</v-btn>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col cols="2">
                  <v-btn color="primary" medium @click="validate(currentAuthenticatedDomain.senderAuth)"
                    :loading="isReauthenticating">
                    Revalidate Sender
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <!--
            Link Details
          -->
          <v-card class="mx-auto" min-width="100%">
            <v-card-text>
              <v-card-title>
                <h2 class="text-h5 text-center">Link Details</h2>
              </v-card-title>
              <v-row>
                <v-col cols="4">
                  <!-- Domain Valid -->
                  <v-list-item>
                    <v-list-item-content two-line>
                      <v-list-item-title>
                        Link is Valid
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span :class="currentAuthenticatedDomain.linkValid ? 'green--text' : 'red--text'">
                          {{ currentAuthenticatedDomain.linkValid | boolToYesNo }}
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Domain Validated At -->
                  <v-list-item>
                    <v-list-item-content two-line>
                      <v-list-item-title>Link Last Validated At</v-list-item-title>
                      <v-list-item-subtitle>
                        <span>
                          {{ currentAuthenticatedDomain.linkLastValidationAttemptAt | timestampToDateTime }}
                        </span>
                        &nbsp;
                        <v-chip small>
                          {{ currentAuthenticatedDomain.linkLastValidationAttemptAt | timestampToRelativeTime }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Sendgrid ID -->
                  <v-list-item v-if="currentAuthenticatedDomain.linkConfigured">
                    <v-list-item-content two-line>
                      <v-list-item-title>
                        Link Sendgrid ID
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ currentAuthenticatedDomain.linkAuth.sendgrid_id }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Subdomain -->
                  <v-list-item v-if="currentAuthenticatedDomain.linkConfigured">
                    <v-list-item-content two-line>
                      <v-list-item-title>
                        Link Subdomain
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ currentAuthenticatedDomain.linkAuth.subdomain }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Automatic Security -->
                </v-col>
                <!-- DNS -->
                <v-col cols="6" v-if="currentAuthenticatedDomain.linkConfigured">
                  <h3>Link DNS</h3>
                  <v-list-item v-for="(record, key, idx) in currentAuthenticatedDomain.linkAuth.dns" :key="idx">
                    <v-list-item-content three-line>
                      <v-list-item-title>
                        <v-chip label small v-if="record.valid" color="green lighten-1" text-color="white">Valid</v-chip>
                        <v-chip label small v-else color="red lighten-1" text-color="white">Invalid</v-chip>
                        {{ key }}
                        <v-chip small label>{{ record.type | uppercase }}</v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="mb-2 mt-2">
                        <span class="font-weight-bold mr-1">DNS Key: </span>{{ record.host }}
                        <v-btn x-small
                          @click="copyToClipboard(extractSubdomain(record.host, currentAuthenticatedDomain.domain))">Copy</v-btn>
                      </v-list-item-subtitle>
                      <v-list-item-subtitle>
                        <span class="font-weight-bold mr-1">DNS Value:</span>{{ record.data }}
                        <v-btn x-small @click="copyToClipboard(record.data)">Copy</v-btn>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col cols="2">
                  <v-btn color="primary" medium @click="validate(currentAuthenticatedDomain.linkAuth)"
                    :loading="isReauthenticating">
                    Revalidate Link
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-sheet>
      </v-bottom-sheet>
    </div>
  </v-card>
</template>

<script setup>
import { AuthenticatedDomainsApi } from '@/services'
import RefreshSpinner from '@/components/ui/RefreshSpinner'
import useSearchFilters from '@/composables/useSearchFilters'
import useCopyText from '@/composables/useCopyText'
import { ref, computed, onMounted, watch } from 'vue'
import { groupBy, uniq, concat } from 'ramda'

const copyToClipboard = useCopyText()

const { filterDataBySearchTerm } = useSearchFilters()
const search = ref('')
const propsToSearch = ref(['domain'])
const isRefreshingData = ref(false)
const isReauthenticating = ref(false)
const authenticatedSenders = ref([])
const authenticatedLinks = ref([])
const tableData = ref([])
const sheet = ref(false)
const currentAuthenticatedDomain = ref({})

const tableColumnsStatic = ref([
  {
    text: 'Domain',
    value: 'domain'
  },
  {
    text: 'Sender Auth Configured',
    value: 'senderConfigured'
  },
  {
    text: 'Sender Auth Valid',
    value: 'senderValid'
  },
  {
    text: 'Sender Last Validated At',
    value: 'senderLastValidationAttemptAt'
  },
  {
    text: 'Link Auth Configured',
    value: 'linkConfigured'
  },
  {
    text: 'link Auth Valid',
    value: 'linkValid'
  },
  {
    text: 'Link Last Validated At',
    value: 'linkLastValidationAttemptAt'
  },
  {
    text: "Actions",
    value: "actions",
  }
])

const recordActions = ref([
  { title: 'View', action: 'view', icon: 'mdi-binoculars' },
])

const queriedData = computed(() => {
  if (!search.value) {
    return tableData.value
  } else {
    return filterDataBySearchTerm(search.value, propsToSearch.value, tableData.value)
  }
})

watch(sheet, (newVal) => {
  if (newVal === false) {
    currentAuthenticatedDomain.value = {}
  }
})

// this didn't work as a tableData computed value  - it would lose the link data somewhere I could not track down
async function mergeAuthDomainData(senders, links) {
  if (senders.length === 0 && links.length === 0) {
    return []
  }

  const groupedSenders = groupBy((sender) => { return sender.domain }, senders)
  const groupedLinks = groupBy((link) => { return link.domain }, links)

  const domains = uniq(concat(Object.keys(groupedSenders), Object.keys(groupedLinks)))

  const mergedData = domains.map((domain) => {
    const senderData = groupedSenders[domain] ? groupedSenders[domain][0] : {}
    const linkData = groupedLinks[domain] ? groupedLinks[domain][0] : {}

    const authDomainData = {
      domain: domain,
      senderConfigured: senderData.domain ? true : false,
      senderValid: senderData.domain_valid,
      senderLastValidationAttemptAt: senderData.last_validation_attempt_at,
      linkConfigured: linkData.domain ? true : false,
      linkValid: linkData.domain_valid,
      linkLastValidationAttemptAt: linkData.last_validation_attempt_at,
      senderAuth: senderData,
      linkAuth: linkData,
    }
    return authDomainData
  })
  return mergedData
}


async function refreshList() {
  isRefreshingData.value = true
  try {
    const p1 = AuthenticatedDomainsApi.fetchAuthenticatedSenders()
    const p2 = AuthenticatedDomainsApi.fetchAuthenticatedLinks()

    authenticatedSenders.value = (await p1).data
    authenticatedLinks.value = (await p2).data

    tableData.value = await mergeAuthDomainData(authenticatedSenders.value, authenticatedLinks.value)
  } finally {
    isRefreshingData.value = false
  }
}

function executeAction(action, item) {
  return this[action](item);
}

function view(item) {
  currentAuthenticatedDomain.value = item
  sheet.value = true
}

async function validate(item) {
  isReauthenticating.value = true
  try {
    let domainData;
    if (item.type === "AuthenticatedSender") {
      domainData = (await AuthenticatedDomainsApi.revalidateAuthenticatedSender(item)).data
      // merge into main list
      const mergeIndex = authenticatedSenders.value.findIndex((sender) => {
        return sender.sendgrid_id === item.sendgrid_id
      })
      authenticatedSenders.value.splice(mergeIndex, 1, domainData)
    } else {
      domainData = (await AuthenticatedDomainsApi.revalidateAuthenticatedLink(item)).data
      // merge into main list
      const mergeIndex = authenticatedLinks.value.findIndex((link) => {
        return link.sendgrid_id === item.sendgrid_id
      })
      authenticatedLinks.value.splice(mergeIndex, 1, domainData)
    }

    tableData.value = await mergeAuthDomainData(authenticatedSenders.value, authenticatedLinks.value)

    currentAuthenticatedDomain.value = tableData.value.find((domain) => {
      return domain.domain === domainData.domain
    })
  } finally {
    isReauthenticating.value = false
  }
}

async function revalidateAll() {
  isReauthenticating.value = true
  try {
    const p1 = AuthenticatedDomainsApi.revalidateAllAuthenticatedSenders()
    const p2 = AuthenticatedDomainsApi.revalidateAllAuthenticatedLinks()

    const senders = (await p1).data
    const links = (await p2).data

    authenticatedSenders.value = senders
    authenticatedLinks.value = links

    tableData.value = await mergeAuthDomainData(senders, links)
  } finally {
    isReauthenticating.value = false
  }
}

function extractSubdomain(host, domain) {
  return host.replace(`.${domain}`, '')
}

onMounted(async () => {
  refreshList()
})
</script>
